import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, Chip, CircularProgress,
  List, ListItem, ListItemText, Divider, Grid, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, ToggleButton, ToggleButtonGroup, Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import MapIcon from '@mui/icons-material/Map';
import { searchElectorates } from '../services/api';
import YearRangeSlider from './YearRangeSlider';

const currentYear = new Date().getFullYear();

export default function ElectoratesSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    name: '',
    state: '',
    origin: '',
    includeHistorical: true
  });
  const [establishedRange, setEstablishedRange] = useState([1901, currentYear]);

  // Shared state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  // Default Status=Current so first load shows current electorates only,
  // but the Historical option + (global) count still renders in the sidebar.
  const [activeFilters, setActiveFilters] = useState({ status: ['Current'] });
  const [computedFacets, setComputedFacets] = useState({});
  const [availableStates, setAvailableStates] = useState([]);
  // Established-year sidebar filter (mirrors the advanced-form slider).
  // null = inactive (no date clause appended).
  const [establishedFilter, setEstablishedFilter] = useState(null);
  // A-Z multi-select chip selections (uppercase letters + '#' for non-alpha)
  const [selectedLetters, setSelectedLetters] = useState([]);
  // Per-letter counts from the backend `first_letter` agg (global, unfiltered).
  const [letterCounts, setLetterCounts] = useState([]);

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchElectorates('*', 1, 100);
        if (data.hits) {
          const states = [...new Set(data.hits.map(h => h.State).filter(Boolean))].sort();
          setAvailableStates(states);
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Run search on initial load
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      // Perform default search on load
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive client-side fallback (legacy shape) used only if the backend
  // does not return a `facets` block. New backend agg pattern is the source
  // of truth — see backend/main.py /api/electorates/search.
  const computeFacetsFromResults = (hits) => {
    const facets = {
      status: {},
      states: {}
    };

    hits.forEach(hit => {
      if (hit.State) facets.states[hit.State] = (facets.states[hit.State] || 0) + 1;
      const status = hit.Ceased ? 'Historical' : 'Current';
      facets.status[status] = (facets.status[status] || 0) + 1;
    });

    // Preserve insertion order: status first, then states.
    const result = {};
    ['status', 'states'].forEach((name) => {
      const counts = facets[name] || {};
      result[name] = Object.entries(counts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count);
    });
    return result;
  };

  // Order facets for sidebar render: Status (top), States, then everything else.
  // first_letter is rendered as A-Z chips above the search bar, not in the
  // sidebar accordion list.
  const orderFacets = (facets) => {
    if (!facets) return {};
    const ordered = {};
    ['status', 'states'].forEach((k) => {
      if (Array.isArray(facets[k]) && facets[k].length > 0) ordered[k] = facets[k];
    });
    Object.entries(facets).forEach(([k, v]) => {
      if (k === 'status' || k === 'states' || k === 'first_letter') return;
      if (Array.isArray(v) && v.length > 0) ordered[k] = v;
    });
    return ordered;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm({ ...form, [field]: value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.name) parts.push(`Electorate:"${form.name}"`);
    if (form.state) parts.push(`State:"${form.state}"`);
    if (form.origin) parts.push(`Origin:"${form.origin}"`);

    if (!form.includeHistorical) {
      parts.push(`NOT Ceased:*`);
    }

    if (establishedRange[0] > 1901 || establishedRange[1] < currentYear) {
      parts.push(`Established:[${establishedRange[0]}-01-01 TO ${establishedRange[1]}-12-31]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters, letters = selectedLetters, dateRange = establishedFilter) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        if (field === 'status') {
          // Status is derived from Ceased presence; OR multiple selections.
          const statusClauses = values
            .map(v => v === 'Current' ? 'NOT _exists_:Ceased' : (v === 'Historical' ? '_exists_:Ceased' : null))
            .filter(Boolean);
          if (statusClauses.length === 1) {
            filterParts.push(statusClauses[0]);
          } else if (statusClauses.length > 1) {
            filterParts.push(`(${statusClauses.join(' OR ')})`);
          }
        } else {
          const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
          filterParts.push(`(${fieldQuery})`);
        }
      }
    });

    // A-Z letter chips → OR over Electorate:<L>*
    if (Array.isArray(letters) && letters.length > 0) {
      const letterClauses = letters
        .filter(l => /^[A-Z]$/.test(l))
        .map(l => `Electorate:${l}*`);
      if (letterClauses.length > 0) {
        filterParts.push(letterClauses.length === 1 ? letterClauses[0] : `(${letterClauses.join(' OR ')})`);
      }
    }

    // Established-year range (sidebar slider) → date-range clause
    if (Array.isArray(dateRange) && dateRange.length === 2) {
      const [lo, hi] = dateRange;
      if (lo > 1901 || hi < currentYear) {
        filterParts.push(`Established:[${lo}-01-01 TO ${hi}-12-31]`);
      }
    }

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (
    searchQuery = null,
    filters = activeFilters,
    letters = selectedLetters,
    dateRange = establishedFilter
  ) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    setLoading(true);
    try {
      const fullQuery = buildQueryWithFilters(baseQuery, filters, letters, dateRange);
      const data = await searchElectorates(fullQuery, 1, 100);
      setResults(data);
      // Prefer backend-supplied global facets (corpus-wide, exclude own filter).
      // Fall back to legacy client-side counts only if the API didn't return them.
      const serverFacets = data && data.facets && Object.keys(data.facets).length > 0
        ? data.facets
        : null;
      const sidebarFacets = serverFacets ? orderFacets(serverFacets) : computeFacetsFromResults(data.hits);
      setComputedFacets(sidebarFacets);
      // Expose first_letter counts separately for the A-Z chip row (when present).
      if (serverFacets && Array.isArray(serverFacets.first_letter)) {
        setLetterCounts(serverFacets.first_letter);
      }
      setSearchParams({ q: baseQuery, mode: searchMode });
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      name: '',
      state: '',
      origin: '',
      includeHistorical: true
    });
    setEstablishedRange([1901, currentYear]);
    setActiveFilters({});
    setSelectedLetters([]);
    setEstablishedFilter(null);
    setResults(null);
    setSearchParams({});
  };

  // Toggle an A-Z chip and immediately re-search.
  const handleLetterToggle = (letter) => {
    const next = selectedLetters.includes(letter)
      ? selectedLetters.filter(l => l !== letter)
      : [...selectedLetters, letter];
    setSelectedLetters(next);
    handleSearch(null, activeFilters, next, establishedFilter);
  };

  // Sidebar Established-year slider — debounced via onChangeCommitted in JSX.
  const handleEstablishedRangeCommit = (newRange) => {
    const lo = newRange[0];
    const hi = newRange[1];
    // Treat a full-span value as "no filter" so we don't append a redundant clause.
    const next = (lo === 1901 && hi === currentYear) ? null : [lo, hi];
    setEstablishedFilter(next);
    handleSearch(null, activeFilters, selectedLetters, next);
  };

  const facetToField = (facetName) => {
    const mapping = {
      'states': 'State',
      'status': 'status'
    };
    return mapping[facetName] || facetName;
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, newFilters, selectedLetters, establishedFilter);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setSelectedLetters([]);
    setEstablishedFilter(null);
    handleSearch(null, {}, [], null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">Electorates</Typography>
          <Button
            component={RouterLink}
            to="/electorates/map"
            variant="outlined"
            size="small"
            startIcon={<MapIcon />}
            sx={{ borderRadius: 2 }}
          >
            View All on Map
          </Button>
        </Box>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search electorates by name, state, origin..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Electorate Name"
                value={form.name}
                onChange={handleChange('name')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableStates}
                value={form.state}
                onChange={handleAutocompleteChange('state')}
                renderInput={(params) => <TextField {...params} label="State" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Name Origin"
                value={form.origin}
                onChange={handleChange('origin')}
                placeholder="e.g., Aboriginal, person name"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Include Historical</InputLabel>
                <Select
                  value={form.includeHistorical ? 'yes' : 'no'}
                  onChange={(e) => setForm({ ...form, includeHistorical: e.target.value === 'yes' })}
                  label="Include Historical"
                >
                  <MenuItem value="yes">Include Historical (Ceased)</MenuItem>
                  <MenuItem value="no">Current Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Established Year"
                value={establishedRange}
                onChange={setEstablishedRange}
                min={1901}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(computedFacets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedLetters.length > 0 || establishedFilter) && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {Object.entries(computedFacets).map(([facetName, facetValues]) => (
                  facetValues.length > 0 && (
                    <Accordion key={facetName} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                          {facetName.replace('_', ' ')}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facetValues.map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetToField(facetName)]?.includes(facet.value) || false}
                                onChange={() => handleFacetClick(facetToField(facetName), facet.value)}
                              />
                              <ListItemText
                                primary={`${facet.value} (${facet.count})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}

                {/* Established Year (sidebar mirror of the advanced-form slider).
                    Date-range UX matches HansardSearch.js — same YearRangeSlider. */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Established Year
                      {establishedFilter && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({establishedFilter[0]}–{establishedFilter[1]})
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <YearRangeSlider
                      label="Range"
                      value={establishedFilter || [1901, currentYear]}
                      onChange={(v) => setEstablishedFilter((v[0] === 1901 && v[1] === currentYear) ? null : v)}
                      min={1901}
                      max={currentYear}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleEstablishedRangeCommit(establishedFilter || [1901, currentYear])}
                      >
                        Apply
                      </Button>
                      {establishedFilter && (
                        <Button
                          size="small"
                          onClick={() => { setEstablishedFilter(null); handleSearch(null, activeFilters, selectedLetters, null); }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Starts with — A-Z multi-select chips (moved from above the
                    search bar into the filter rail per user feedback
                    2026-06-01). Counts come from the backend global agg
                    (`first_letter`) so they stay corpus-wide. */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Starts with
                      {selectedLetters.length > 0 && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({selectedLetters.join(', ')})
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((letter) => {
                        const bucket = letterCounts.find(b => b.value === letter);
                        const count = bucket ? bucket.count : 0;
                        const selected = selectedLetters.includes(letter);
                        const disabled = letterCounts.length > 0 && count === 0;
                        return (
                          <Chip
                            key={letter}
                            label={count > 0 ? `${letter} (${count})` : letter}
                            size="small"
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => !disabled && handleLetterToggle(letter)}
                            disabled={disabled}
                            sx={{
                              minWidth: 36,
                              cursor: disabled ? 'default' : 'pointer',
                              opacity: disabled ? 0.4 : 1
                            }}
                          />
                        );
                      })}
                    </Box>
                    {selectedLetters.length > 0 && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => { setSelectedLetters([]); handleSearch(null, activeFilters, [], establishedFilter); }}
                      >
                        Clear letters
                      </Button>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(computedFacets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {results.total} electorates found
              </Typography>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/electorates/${hit.EID}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {hit.Electorate}
                            </Typography>
                            <Chip label={hit.State} size="small" />
                            {hit.Ceased && (
                              <Chip label="Historical" size="small" variant="outlined" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {hit.Origin && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {hit.Origin}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Established: {formatDate(hit.Established)}{hit.Ceased && ` • Ceased: ${formatDate(hit.Ceased)}`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
